// MACHINERY V2 (Stroje) — ChromeNavV4 module table + visibility gate (M2.1).
//
// The nav's configuration, split out of ChromeNavV4.tsx so the bar stays
// presentational and this table is the ONE place the M2.12 promotion edits
// (paths, ordering, a new module, the accent source).
//
// >>> BOTH VISIBILITY GATES LIVE HERE, AND NEITHER MAY BE FORKED. <<< A nav
// entry is shown when `canSeeModule` (permission) AND `useTypeAllows`
// (company type) both pass. They sit together because they are ONE decision
// asked in two halves, and because a gate with two copies is a permission bug
// waiting for the M2.12 promotion: the copies drift, one surface keeps showing
// a module the other has already hidden, and the transition escape hatch is
// exactly the kind of clause that gets "tidied away" from a copy. M2.16a
// shipped `typeAllows` forked verbatim into MobileDrawer.tsx; this is the
// un-fork.
//
// Accents are the approved module colours from
// design_handoff_machinery_v3/palette-tokens.css, carried here exactly as the
// handoff's NAV_ACCENTS map carries them. Stroje reads the scoped CSS var
// because a v2 page always has .mod-stroje on its root; the three siblings
// carry their approved hex. That is why the .mod-servicehub / .mod-network /
// .mod-operacie CSS blocks stay dropped from machinery-v2-tokens.scss — see
// the marker comment there, and re-open it at M2.12.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import permissionContext from "../../../../core/context/permissionContext";
import { Modules, PermissionLevel } from "../../../../core/models/PermissionModels";
import useCompanyType from "../../../../shared/hooks/useCompanyType";
import { NgMachine, NgNetwork, NgOps, NgService } from "./ChromeNavIcons";

export type ChromeNavModuleKey = "stroje" | "servicehub" | "network" | "operacie";

export interface NavModule {
  key: ChromeNavModuleKey;
  labelKey: string;
  path: string;
  module: string;
  /** Active-tab + avatar tint. Inactive tabs are never tinted. */
  accent: string;
  Icon: React.FC;
  /** Mirrors the legacy bar's visibleFor:"operator" sections. */
  operatorOnly: boolean;
}

/** M2.12: flip to "/machinery" when v2 becomes the default surface. */
export const STROJE_PATH = "/machinery-v2";

export const NAV_MODULES: NavModule[] = [
  {
    key: "stroje",
    labelKey: "layout:chromeV4.stroje",
    path: STROJE_PATH,
    module: Modules.Machinery,
    accent: "var(--mod-accent)",
    Icon: NgMachine,
    operatorOnly: true,
  },
  {
    key: "servicehub",
    labelKey: "layout:chromeV4.servicehub",
    path: "/service-board",
    module: Modules.Servicing,
    accent: "#33739E",
    Icon: NgService,
    operatorOnly: false,
  },
  {
    key: "network",
    labelKey: "layout:chromeV4.network",
    path: "/companies",
    module: Modules.PartnerHub,
    accent: "#8A5A83",
    Icon: NgNetwork,
    operatorOnly: false,
  },
  {
    key: "operacie",
    labelKey: "layout:chromeV4.operacie",
    path: "/operations",
    module: Modules.Maintenance,
    accent: "#A8763E",
    Icon: NgOps,
    operatorOnly: true,
  },
];

/** Read gate with the legacy TRANSITION-MODE escape hatch: a token carrying NO
 *  permissions claim sees everything, exactly as Header.tsx:424 does. Reads the
 *  current JWT on every call, so it is correct across a token refresh. */
export const canSeeModule = (module: string): boolean =>
  !permissionContext.hasPermissionsClaim() ||
  permissionContext.hasPermission(module, PermissionLevel.Read);

/**
 * Company-type gate for `NavModule.operatorOnly`, mirroring the legacy bar's
 * `visibleFor: "operator"` sections — WITH the same transition escape hatch the
 * legacy shell applies: `companyType === 0` means "no company type in the
 * token" (a legacy or not-yet-typed account). Header.tsx:388-395
 * (isVisibleForType) and TypeGuard both let such a token through, so it DOES
 * reach /machinery-v2; gating it out would hide Stroje — the page it is
 * standing on — and Operacie, with no active tab and no way back, while the
 * legacy bar above still shows both.
 *
 * A HOOK, not a plain predicate, on purpose: it owns WHICH INPUTS feed the
 * gate, not just the boolean expression. A call site that had to pass
 * `companyType` and `isOperator` itself could still pass the wrong pair, which
 * is the same fork risk one level down.
 */
export const useTypeAllows = (): ((operatorOnly: boolean) => boolean) => {
  const { companyType, isOperator } = useCompanyType();
  return (operatorOnly: boolean) => !operatorOnly || companyType === 0 || isOperator;
};
